import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, QueryList } from '@angular/core';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PropertyResponse } from '../models/property.model';
import { AmenitiesResponse, ProvincesResponse } from '../models/category';
import { SubCategoryResponse } from '../../core/models/category';
import { DashboardResponse } from '../models/dashboardResponse';
import { IProperty } from '../models/property.interface';
import { FilterForm } from '../models/propertyFilter.interface';
import { ISearchHistory } from '../models/search-history.interface';
import { Filter } from '../../pages/after-login-page/properity-listing/interfaces/filter.interface';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(private readonly httpClient: HttpClient) {}
  dashboardData: BehaviorSubject<DashboardResponse | null> =
    new BehaviorSubject<DashboardResponse | null>(null);

  public getHomePageData(body: string, guestProvince: string = ''): Observable<DashboardResponse> {
    const options = guestProvince
      ? { params: { province: guestProvince } }
      : {};

    return this.httpClient.post<DashboardResponse>(
      `${environment.base_url}dashboard`, body, options
    );
  }

  public getFilters(): Observable<Filter> {
    return this.httpClient.get<Filter>(`${environment.base_url}getFilters`);
  }

  public getSearchedFilteredData(
    query: string,
    payload: FilterForm,
    province?: string[]
  ): Observable<PropertyResponse> {
    const url = query ? `${environment.base_url}globalSearch?search=${query}` : `${environment.base_url}globalSearch`;
    return this.httpClient
      .post<{
        data: PropertyResponse;
      }>(url, payload)
      .pipe(map(res => res.data));
  }

  public getSearchHistory(): Observable<{
    searchTexts: ISearchHistory[];
  }> {
    return this.httpClient
      .get<{
        data: { searchTexts: ISearchHistory[] };
      }>(`${environment.base_url}getSearchHistory`)
      .pipe(map(res => res.data));
  }

  public getCategories(): Observable<unknown> {
    return this.httpClient.get<unknown>(`${environment.base_url}category`);
  }

  public getSubCategories(categoryId: string): Observable<SubCategoryResponse> {
    return this.httpClient.get<SubCategoryResponse>(
      `${environment.base_url}subCategory/${categoryId}`
    );
  }

  public getSubCategoriesForFilter(
    categoryIds: string[]
  ): Observable<SubCategoryResponse> {
    return this.httpClient.post<SubCategoryResponse>(
      `${environment.base_url}subCategoryForFilter`,
      { category: categoryIds }
    );
  }

  public getSubCategoriesData(subCategoryId: string): Observable<unknown> {
    return this.httpClient.get<unknown>(
      `${environment.base_url}property?subCategory=${subCategoryId}`
    );
  }

  public getProvinces(): Observable<ProvincesResponse> {
    return this.httpClient.get<ProvincesResponse>(`${environment.base_url}province`)
      .pipe(
        map(response => ({
          ...response,
          data: {
            ...response.data,
            provinces: [...response.data.provinces].sort((a, b) =>
              a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
            ),
          },
        }))
      );
  }

  public getAmenities(): Observable<AmenitiesResponse> {
    return this.httpClient.get<AmenitiesResponse>(
      `${environment.base_url}amenities`
    );
  }

  public getNewAreaFeatured(): Observable<{
    totalProperty: number;
    properties: IProperty[];
  }> {
    return this.httpClient
      .get<{
        data: {
          totalProperty?: number;
          totalAdvertisements?: number;
          property?: IProperty[];
          advertisements?: IProperty[];
        };
      }>(`${environment.base_url}getNewAreaFeatured`)
      .pipe(
        map(res => ({
          totalProperty:
            res.data.totalProperty ??
            res.data.totalAdvertisements ??
            (res.data.advertisements?.length || res.data.property?.length || 0),
          properties: res.data?.advertisements || res.data?.property || []
        }))
      );
  }

  public getDashboardFeaturedProperties(): Observable<IProperty[]> {
    const cachedFeatured = this.dashboardData.value?.data?.newYourAreaFeatured;

    if (cachedFeatured?.length) {
      return of(cachedFeatured);
    }

    return this.getHomePageData(
      '',
      sessionStorage.getItem('guestProvinceId') || ''
    ).pipe(
      tap(res => this.dashboardData.next(res)),
      map(res => res.data.newYourAreaFeatured ?? [])
    );
  }

  public getNewAreaProperties(): Observable<{ totalProperty: number; properties?: IProperty[] }> {
    return this.httpClient.get<{ data: { totalProperty: number; property: IProperty[] } }>(`${environment.base_url}getNewAreaProperties`)
    .pipe(
      map(res => ({
        totalProperty: res.data.totalProperty,
        properties: res.data?.property || []
        }))
    );
  }
  
  getPropertiesOfSubCategoryAndCategory(query: string): Observable<{
    nearestProperties: IProperty[];
    property: IProperty[];
    features?: IProperty[];
    totalProperty: number;
  }> {
    return this.httpClient
      .get<{
        data: {
          nearestProperties: IProperty[];
          property: IProperty[];
          totalProperty: number;
        };
      }>(`${environment.base_url}property?${query}`)
      .pipe(map(response => response.data));
  }
  // public getSubCategories(categoryId: string): Observable<unknown> {
  //   return this.httpClient.get<unknown>(
  //     `${environment.base_url}subCategory/${categoryId}`
  //   );
  // }

  public getPromotions(): Observable<PropertyResponse> {
    return this.httpClient
      .get<{ data: PropertyResponse }>(`${environment.base_url}promotions`)
      .pipe(map(res => res.data));
  }
}
